import { QuoteLineItemsPayload } from './quoteDesignSnapshot'
import { readQuoteLineItemsFromStudioSystem } from './quoteLineItemsFromStudioSystem'
import { waitForStudioReady } from './syncQuoteSystemDesignIntoStudio'

const normUuid = (u: string | null | undefined) => (u || '').toLowerCase()

export type QuotePlaceholderSystem = {
  uuid: string
  id?: number | null
  name: string
  lineItems: QuoteLineItemsPayload
}

/** Poll until `window.editor` exposes the Studio API (shorter gate than legacy 15s ensure path). */
export async function waitForQuoteStudioEditor(maxWaitMs = 8000): Promise<boolean> {
  return waitForStudioReady(maxWaitMs)
}

/** Resolve the quote-page OsSystem from the live editor (first customer-visible system). */
export function findQuoteSystemInEditor(preferredUuid?: string | null): any | null {
  const w = window as any
  const editor = w.editor
  const systems: any[] = editor?.getSystems?.() ?? []
  if (!systems.length) return null

  if (preferredUuid) {
    const match = systems.find((s) => normUuid(s?.uuid) === normUuid(preferredUuid))
    if (match) return match
  }

  const visible = systems.find((s) => s?.show_customer !== false)
  return visible ?? systems[0] ?? null
}

/**
 * Single shared resolver for the live Studio quote `OsSystem` — the canonical source the Quote
 * tab edits. Used by `useQuoteStudioSystem` (pro Quote/Payments) and the Proposal pro-side path so
 * every quote read goes through one live-editor read instead of divergent per-call logic.
 */
export function resolveLiveQuoteSystem(preferredUuid?: string | null): any | null {
  return findQuoteSystemInEditor(preferredUuid)
}

/**
 * GS-7407: Create a hardware-free placeholder ``OsSystem`` client-side when the Quote page
 * mounts and Studio has no system yet. Mirrors ``SceneHelper.createFirstSystem`` but seeds
 * non-solar defaults (``QU-{projectId}``, zero kW) instead of a default module grid.
 */
export function createQuotePlaceholderSystem(projectId: string | number): QuotePlaceholderSystem | null {
  const w = window as any
  const editor = w.editor
  if (!editor?.execute || !w.OsSystem || !w.AddObjectCommand) return null

  const existing = findQuoteSystemInEditor()
  if (existing) {
    return buildQuotePlaceholderInfo(existing, projectId)
  }

  const options: Record<string, unknown> = {
    show_customer: true,
    name: `QU-${projectId}`,
    order: editor.orderForNextSystem?.() ?? 0,
    kw_stc: 0,
    battery_total_kwh: 0,
    moduleQuantity: 0,
    // Match Design → Pricing "Enter Price Manually $"; line items on the Quote page set price.
    pricing_scheme_id: null,
    costing_override: null,
    // Never auto-apply org pricing/costing/adders — quick-quote systems must start at $0.
    autoSync: {
      pricingScheme: false,
      costing: false,
      adders: false,
    },
  }

  if (w.Utils?.addPaymentOptionCalcsVersionToPricingOptions) {
    w.Utils.addPaymentOptionCalcsVersionToPricingOptions(options)
  }

  try {
    const newSystem = new w.OsSystem(options)

    if (!Array.isArray(newSystem.incentives)) {
      newSystem.incentives = []
    }
    if (!Array.isArray(newSystem.incentive_to_installer)) {
      newSystem.incentive_to_installer = []
    }
    if (!Array.isArray(newSystem.line_items)) {
      newSystem.line_items = []
    }

    editor.execute(new w.AddObjectCommand(newSystem, undefined, true))
    return buildQuotePlaceholderInfo(newSystem, projectId)
  } catch (e) {
    console.warn('[createQuotePlaceholderSystem] failed', e)
    return null
  }
}

const buildQuotePlaceholderInfo = (system: any, projectId: string | number): QuotePlaceholderSystem => {
  const uuid = system.uuid as string
  const dbId = typeof system.id === 'number' && system.id > 0 ? system.id : system.userData?.id ?? null
  const name = (system.name as string) || `QU-${projectId}`
  return {
    uuid,
    id: dbId,
    name,
    lineItems: readQuoteLineItemsFromStudioSystem(system),
  }
}
